import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  signal,
  inject,
} from '@angular/core';
import Chart from 'chart.js/auto';
import { ChartDatasets } from './charts.model';
import { ReportService } from '../../services/report/report.service';

@Component({
  selector: 'lib-charts-wrapper',
  imports: [],
  templateUrl: './charts-wrapper.component.html',
  styleUrl: './charts-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ChartsWrapperComponent implements OnChanges, OnDestroy {
  private readonly reportService = inject(ReportService);

  type = input.required<
    'doughnut' | 'bar' | 'line' | 'pie' | 'polarArea' | 'radar'
  >();
  id = input.required<string>();
  labels = input.required<string[]>();
  datasets = input.required<ChartDatasets[]>();
  title = input<string>('');
  value = input<number | string | undefined>(undefined);
  disableChartInternalLabels = input<boolean>(false);
  doughnutChartCutout = input<number>(85);
  height = input<number>(160);
  width = input<number>(160);
  animation = input<boolean>(true);
  isHalfDoughnut = input<boolean>(false);

  chart: Chart | undefined;
  clearTimeout = signal<ReturnType<typeof setTimeout> | undefined>(undefined);

  ngOnChanges(changes: SimpleChanges): void {
    // Drop any pending update, otherwise stacked timeouts init the same canvas twice.
    if (this.clearTimeout()) {
      clearTimeout(this.clearTimeout());
    }
    this.clearTimeout.set(
      setTimeout(() => {
        this.clearTimeout.set(undefined);
        this.handleChartChange(changes);
      }, 0)
    );
  }

  handleChartChange(changes: SimpleChanges): void {
    if (changes['datasets'] && changes['datasets'].currentValue.length) {
      this.initChart();
    } else if (
      !changes['datasets'] ||
      !changes['datasets'].currentValue.length ||
      this.chart
    ) {
      this.destroyChart();
    }
  }

  initChart(): void {
    const canvasId = 'chart-' + this.id();
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;

    // Destroy existing chart if any
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }

    // Chart.js keeps its own canvas registry, so a chart left over from a previous
    // render must be released before the same canvas can be reused.
    const chartOnCanvas = canvas ? Chart.getChart(canvas) : undefined;
    if (chartOnCanvas) {
      chartOnCanvas.destroy();
    }

    const chartOptions: any = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.disableChartInternalLabels(),
        },
        tooltip: {
          backgroundColor: '#111827',
          boxPadding: this.reportService.getCurrentValueInPixels(0.25),
          titleFont: {
            size: this.reportService.getCurrentValueInPixels(0.625),
          },
          bodyFont: {
            size: this.reportService.getCurrentValueInPixels(0.7),
          },
        },
      },
    };

    // Add cutout option only for doughnut and pie charts
    if (this.type() === 'doughnut' || this.type() === 'pie') {
      chartOptions.cutout = this.doughnutChartCutout();
      chartOptions.plugins.animation = this.animation();
      chartOptions.transitions = this.animation();
      if (this.isHalfDoughnut()) {
        chartOptions.rotation = -90;
        chartOptions.circumference = 180;
      }
    }

    // Configure scales for bar and line charts
    if (this.type() === 'bar' || this.type() === 'line') {
      chartOptions.scales = {
        y: {
          beginAtZero: true,
        },
      };
      chartOptions.transitions = this.animation();
    }

    if (canvas) {
      this.chart = new Chart(canvas, {
        type: this.type(),
        data: {
          labels: this.labels(),
          datasets: this.resolveDatasetColors(this.datasets()),
        },
        options: chartOptions,
      });
    }
  }

  private resolveDatasetColors(datasets: ChartDatasets[]): ChartDatasets[] {
    if (!datasets.some(dataset => this.datasetUsesCssColor(dataset))) {
      return datasets;
    }

    return datasets.map(dataset => ({
      ...dataset,
      backgroundColor: this.resolveColors(dataset.backgroundColor),
      borderColor: this.resolveColors(dataset.borderColor),
    }));
  }

  private datasetUsesCssColor(dataset: ChartDatasets): boolean {
    return [dataset.backgroundColor, dataset.borderColor].some(colors =>
      colors?.some(color => color.startsWith('var('))
    );
  }

  private resolveColors(colors: string[] | undefined): string[] | undefined {
    return colors?.map(color => this.resolveColor(color));
  }

  private resolveColor(color: string): string {
    const variableName = color.match(/^var\((--[\w-]+)\)$/)?.[1];
    if (!variableName) {
      return color;
    }

    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();

    return resolved || color;
  }

  destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
    if (this.clearTimeout()) {
      clearTimeout(this.clearTimeout());
      this.clearTimeout.set(undefined);
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }
}
